import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@loot-os/ui/components/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@loot-os/ui/components/sidebar";
import { type LucideIcon, Monitor, Moon, Sun } from "lucide-react";

import { parseThemeChoice, type ThemeChoice, useThemeStore } from "../lib/theme";

const THEME_OPTIONS: Record<ThemeChoice, { label: string; Icon: LucideIcon }> = {
  light: { label: "Light", Icon: Sun },
  system: { label: "System", Icon: Monitor },
  dark: { label: "Dark", Icon: Moon },
};

/** The order the menu items show in. */
const THEME_ORDER: ThemeChoice[] = ["light", "system", "dark"];

/**
 * The Light / System / Dark setting for the sidebar footer: one button with the current choice's
 * icon that opens a menu of the three. Expanded, the button also names the current choice;
 * collapsed to icons, its tooltip does.
 */
export function ThemeToggle() {
  const choice = useThemeStore((s) => s.choice);
  const setChoice = useThemeStore((s) => s.setChoice);
  const { label: currentLabel, Icon: CurrentIcon } = THEME_OPTIONS[choice];

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                tooltip={`Theme: ${currentLabel}`}
                className="data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground"
              >
                <CurrentIcon />
                <span>Theme</span>
                <span className="ml-auto text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
                  {currentLabel}
                </span>
              </SidebarMenuButton>
            }
          />
          <DropdownMenuContent side="right" align="end" sideOffset={4} className="min-w-36">
            <DropdownMenuRadioGroup
              value={choice}
              onValueChange={(value) => setChoice(parseThemeChoice(value))}
            >
              {THEME_ORDER.map((value) => {
                const { label, Icon } = THEME_OPTIONS[value];
                return (
                  <DropdownMenuRadioItem key={value} value={value}>
                    <Icon />
                    {label}
                  </DropdownMenuRadioItem>
                );
              })}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
